import React, { useRef, useEffect } from 'react';
import { Category } from '../types';

interface FilterBarProps {
  categories: Category[];
  activeCategory: string | null;
  onCategorySelect: (category: string | null) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  categories,
  activeCategory,
  onCategorySelect,
}) => {
  const filterRef = useRef<HTMLDivElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const updateArrowVisibility = () => {
    if (!filterRef.current || !prevButtonRef.current || !nextButtonRef.current) return;

    // Hide prev if scrolled to the start
    if (filterRef.current.scrollLeft <= 0) {
      prevButtonRef.current.style.display = 'none';
    } else {
      prevButtonRef.current.style.display = '';
    }

    // Hide next if scrolled to the end
    if (filterRef.current.scrollLeft + filterRef.current.clientWidth >= filterRef.current.scrollWidth - 1) {
      nextButtonRef.current.style.display = 'none';
    } else {
      nextButtonRef.current.style.display = '';
    }
  };

  const scrollToSlideLeft = (target: HTMLElement) => {
    filterRef.current?.scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth'
    });
  };

  const handleNextClick = () => {
    if (!filterRef.current) return;
    const sliderRect = filterRef.current.getBoundingClientRect();
    const chips = Array.from(filterRef.current.querySelectorAll('.chip'));

    for (let chip of chips) {
      const rect = chip.getBoundingClientRect();
      const partlyVisible = rect.left < sliderRect.right && rect.right > sliderRect.right;
      const fullyHidden = rect.left >= sliderRect.right;

      if (partlyVisible || fullyHidden) {
        scrollToSlideLeft(chip as HTMLElement);
        break;
      }
    }

    setTimeout(updateArrowVisibility, 400); // Wait for smooth scroll
  };

  const handlePrevClick = () => {
    if (!filterRef.current) return;
    const sliderRect = filterRef.current.getBoundingClientRect();
    const sliderWidth = filterRef.current.clientWidth;
    const chips = filterRef.current.querySelectorAll('.chip');
    
    let targetSlide: Element | null = null;

    for (let i = chips.length - 1; i >= 0; i--) {
      const chip = chips[i];
      const chipRect = chip.getBoundingClientRect();

      // Check if the slide is off-screen (partially or fully to the left)
      if (chipRect.left < sliderRect.left) {
        targetSlide = chip;
        break;
      }
    }

    if (targetSlide) {
      // Scroll to the first visible slide plus the width of the visible part
      filterRef.current.scrollTo({
        left: (targetSlide as HTMLElement).offsetLeft - sliderWidth + targetSlide.getBoundingClientRect().width,
        behavior: 'smooth'
      });
    }

    setTimeout(updateArrowVisibility, 400); // Wait for smooth scroll
  };

  const handleChipClick = (category: string, event: React.MouseEvent<HTMLDivElement>) => {
    const chip = event.currentTarget;
    const slider = filterRef.current;
    if (!slider) return;

    const sliderRect = slider.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const SCROLL_PADDING = 40; // Add padding for better visibility

    // Check if chip is partially visible
    const partlyVisibleLeft = chipRect.left < sliderRect.left && chipRect.right > sliderRect.left;
    const partlyVisibleRight = chipRect.left < sliderRect.right && chipRect.right > sliderRect.right;

    // If chip is partially visible, scroll just enough to make it fully visible
    if (partlyVisibleLeft || partlyVisibleRight) {
      const currentScroll = slider.scrollLeft;
      
      if (partlyVisibleLeft) {
        // Scroll just enough to align with the left edge plus padding
        slider.scrollTo({
          left: currentScroll - (sliderRect.left - chipRect.left) - SCROLL_PADDING,
          behavior: 'smooth'
        });
      } else {
        // Scroll just enough to align with the right edge plus padding
        slider.scrollTo({
          left: currentScroll + (chipRect.right - sliderRect.right) + SCROLL_PADDING,
          behavior: 'smooth'
        });
      }
      
      // Small delay to ensure the scroll completes before category selection
      setTimeout(() => onCategorySelect(category), 100);
    } else {
      // If fully visible, just select the category
      onCategorySelect(category);
    }
  };

  useEffect(() => {
    // Call on load
    updateArrowVisibility();

    // Add scroll listener
    const slider = filterRef.current;
    if (slider) {
      slider.addEventListener('scroll', updateArrowVisibility);
      return () => slider.removeEventListener('scroll', updateArrowVisibility);
    }
  }, [categories]);

  useEffect(() => {
    if (!filterRef.current || !activeCategory) return;

    const slider = filterRef.current;
    const chips = Array.from(slider.querySelectorAll<HTMLElement>('.chip'));
    const activeChip = chips.find((chip) => chip.dataset.categoryId === activeCategory);

    if (!activeChip) return;

    const sliderRect = slider.getBoundingClientRect();
    const chipRect = activeChip.getBoundingClientRect();
    const isFullyVisible = chipRect.left >= sliderRect.left && chipRect.right <= sliderRect.right;

    if (!isFullyVisible) {
      const targetScroll =
        activeChip.offsetLeft - slider.clientWidth / 2 + activeChip.offsetWidth / 2;

      slider.scrollTo({
        left: Math.max(targetScroll, 0),
        behavior: 'smooth',
      });

      setTimeout(updateArrowVisibility, 400);
    }
  }, [activeCategory, categories.length]);

  return (
    <div className="filters samples__filter">
      <button 
        ref={prevButtonRef}
        className="filters__arrow filters__arrow_prev" 
        onClick={handlePrevClick}
        aria-label="Previous categories"
      >
        <svg width="16" height="28" viewBox="0 0 16 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.7095 26.8532L1.04228 15.1859L0.939386 15.072C0.458832 14.4828 0.493091 13.6138 1.04228 13.0646L12.7095 1.39735C13.2953 0.811561 14.2451 0.811561 14.8309 1.39735C15.4166 1.98313 15.4166 2.93288 14.8309 3.51867L4.22426 14.1253L14.8309 24.7319C15.4166 25.3177 15.4166 26.2674 14.8309 26.8532C14.2451 27.439 13.2953 27.439 12.7095 26.8532Z" fill="currentColor"/>
        </svg>
      </button>
      <div className="filters__inner" ref={filterRef}>
        {categories.map((category) => (
          <div
            key={category.id}
            data-category-id={category.id}
            className={`chip gtm-tattoo-category ${category.id === activeCategory ? 'active' : ''}`}
            onClick={(e) => handleChipClick(category.id, e)}
          >
            {category.name}
          </div>
        ))}
      </div>
      <button 
        ref={nextButtonRef}
        className="filters__arrow filters__arrow_next" 
        onClick={handleNextClick}
        aria-label="Next categories"
      >
        <svg width="15" height="28" viewBox="0 0 15 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.83092 26.8532L14.4982 15.1859L14.6011 15.072C15.0816 14.4828 15.0474 13.6138 14.4982 13.0646L2.83092 1.39735C2.24514 0.811561 1.29539 0.811561 0.709603 1.39735C0.123817 1.98313 0.123817 2.93288 0.709603 3.51867L11.3162 14.1253L0.709603 24.7319C0.123817 25.3177 0.123817 26.2674 0.709603 26.8532C1.29539 27.439 2.24514 27.439 2.83092 26.8532Z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  );
};

export default FilterBar; 